import type { ApiResponse } from "apisauce"

import { request } from "../../../../../services/api"
import { unwrapEnvelope } from "../../../../../utils/api/unwrap-envelope.ts"
import {
  filenameFromContentDisposition,
  saveBlob
} from "../../../../../utils/download-file.ts"
import type {
  ChangeRequest,
  DesignBrief,
  Diagnostics,
  HistoryEvent,
  JobListParams,
  MyPermissions,
  Plan3DJob,
  Plan3DJobRow,
  Plan3DSource,
  ProjectFile,
  RenderingSettings,
  RenderingSettingsInput,
  ReviewMode,
  RoomType,
  SharePackage,
  ViewType
} from "./types.ts"

const BASE_URL = "/plans-3d"
const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } }

// The plans-3d views answer a refusal two ways: their own `Response({code, detail})` arrives bare,
// while a raised exception (403 permission, 404, validation) goes through the app's exception
// handler as `{ message: { detail }, details: { message, code } }`.
interface ErrorBody {
  code?: string
  detail?: string
  message?: { detail?: string } | string
  details?: { message?: string; code?: string }
}

export class ApiError extends Error {
  constructor(
    message: string,
    public code: string | undefined,
    public status: number | undefined
  ) {
    super(message)
    this.name = "ApiError"
  }
}

// A DRF validation 400 is `{field: ["msg"]}` or `["msg"]`; the first message found is shown.
function firstMessage(value: unknown): string | undefined {
  if (typeof value === "string") return value || undefined
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = firstMessage(item)
      if (found) return found
    }
    return undefined
  }
  if (value && typeof value === "object") {
    for (const item of Object.values(value)) {
      const found = firstMessage(item)
      if (found) return found
    }
  }
  return undefined
}

function readError(body: unknown): { detail?: string; code?: string } {
  if (Array.isArray(body)) return { detail: firstMessage(body) }
  if (!body || typeof body !== "object") return {}
  const data = body as ErrorBody
  const nested =
    typeof data.message === "object" ? data.message?.detail : undefined
  const fields = Object.fromEntries(
    Object.entries(data).filter(
      ([key]) =>
        ![
          "code",
          "detail",
          "message",
          "details",
          "type",
          "status_code"
        ].includes(key)
    )
  )
  return {
    detail:
      data.detail || nested || data.details?.message || firstMessage(fields),
    code: data.code || data.details?.code
  }
}

function toError(
  response: ApiResponse<unknown, unknown>,
  body: unknown,
  fallback: string
): ApiError {
  const { detail, code } = readError(body)
  return new ApiError(detail || fallback, code, response.status)
}

async function unwrap<T>(
  promise: Promise<ApiResponse<unknown, unknown>>,
  fallback: string
): Promise<T> {
  const response = await promise
  if (response.ok) {
    return response.data === undefined || response.data === null
      ? (undefined as T)
      : unwrapEnvelope<T>(response.data)
  }
  throw toError(response, response.data, fallback)
}

// A blob-typed request's error body can arrive as a Blob holding the JSON text, or already parsed.
async function blobError(
  response: ApiResponse<unknown, unknown>,
  fallback: string
): Promise<ApiError> {
  let body: unknown = response.data
  if (body instanceof Blob) {
    try {
      body = JSON.parse(await body.text())
    } catch {
      body = undefined
    }
  }
  return toError(response, body, fallback)
}

async function blob(
  path: string,
  params: Record<string, unknown>,
  fallback: string
): Promise<Blob> {
  const response = await request.api.get<Blob, unknown>(path, params, {
    responseType: "blob"
  })
  if (response.ok && response.data instanceof Blob) return response.data
  throw await blobError(response, fallback)
}

async function download(
  path: string,
  params: Record<string, unknown>,
  fallbackName: string
): Promise<void> {
  const response = await request.api.get<Blob, unknown>(path, params, {
    responseType: "blob"
  })
  if (!response.ok || !(response.data instanceof Blob))
    throw await blobError(response, "Download failed")
  const name =
    filenameFromContentDisposition(
      response.headers as Record<string, string> | undefined
    ) || fallbackName
  saveBlob(response.data, name)
}

function form(file: File): FormData {
  const data = new FormData()
  data.append("file", file)
  return data
}

const path = (id: string, rest = "") => `${BASE_URL}/${id}/${rest}`

export const listJobs = (params: JobListParams) => {
  const { archived, ...rest } = params
  return unwrap<Plan3DJobRow[]>(
    request.api.get(`${BASE_URL}/`, archived ? { ...rest, archived: 1 } : rest),
    "Could not load jobs"
  )
}
export const createJob = (body: {
  title: string
  room_type: RoomType
  review_mode?: ReviewMode
  project?: string | null
}) =>
  unwrap<Plan3DJob>(
    request.api.post(`${BASE_URL}/`, body),
    "Could not create the job"
  )
export const getJob = (id: string) =>
  unwrap<Plan3DJob>(request.api.get(path(id)), "Could not load the job")
export const updateJob = (
  id: string,
  body: Partial<{
    title: string
    room_type: RoomType
    project: string | null
    archived: boolean
  }>
) => unwrap<Plan3DJob>(request.api.patch(path(id), body), "Could not save")
export const deleteJob = (id: string) =>
  unwrap<void>(request.api.delete(path(id)), "Could not delete")
export const duplicateJob = (id: string) =>
  unwrap<Plan3DJob>(
    request.api.post(path(id, "duplicate/")),
    "Could not duplicate"
  )

export const uploadSource = (id: string, file: File) =>
  unwrap<Plan3DJob>(
    request.api.post(path(id, "sources/"), form(file), MULTIPART),
    "Upload failed"
  )
export const useProjectFile = (id: string, attachmentId: string) =>
  unwrap<Plan3DJob>(
    request.api.post(path(id, "sources/"), { attachment_id: attachmentId }),
    "Could not use that file"
  )
export const updateSource = (
  id: string,
  sourceId: string,
  body: Partial<{
    page: number
    rotation: number
    room_crop: [number, number, number, number] | null
  }>
) =>
  unwrap<Plan3DSource>(
    request.api.patch(path(id, `sources/${sourceId}/`), body),
    "Could not save"
  )
/** The normalized page as a PNG; `original` serves the uploaded file itself. */
export const fetchSourceImage = (
  id: string,
  sourceId: string,
  original = false
) =>
  blob(
    path(id, `sources/${sourceId}/image/`),
    original ? { original: 1 } : {},
    "Could not load the plan"
  )
export const listProjectFiles = (id: string) =>
  unwrap<ProjectFile[]>(
    request.api.get(path(id, "project-files/")),
    "Could not load project files"
  )

export const startAnalysis = (id: string) =>
  unwrap<Plan3DJob>(request.api.post(path(id, "analysis/")), "Could not start")
export const resolveAssumption = (
  id: string,
  assumptionId: string,
  body: {
    action: "confirm" | "edit" | "reject"
    value?: unknown
    expected_row_version: number
  }
) =>
  unwrap<Plan3DJob>(
    request.api.patch(path(id, `assumptions/${assumptionId}/`), body),
    "Could not save"
  )
/** Confirms every open default (SYSTEM_DEFAULT, and RENDER_ONLY rows) in one scene version (B4). */
export const confirmDefaults = (id: string, rowVersion: number) =>
  unwrap<Plan3DJob>(
    request.api.post(path(id, "assumptions/confirm-defaults/"), {
      expected_row_version: rowVersion
    }),
    "Could not save"
  )
export const approveArchitecture = (id: string, rowVersion: number) =>
  unwrap<Plan3DJob>(
    request.api.post(path(id, "architecture/approve/"), {
      expected_row_version: rowVersion
    }),
    "Could not approve"
  )

export const uploadInspiration = (id: string, file: File) =>
  unwrap<{ id: string; name: string }>(
    request.api.post(path(id, "inspiration/"), form(file), MULTIPART),
    "Upload failed"
  )
export const fetchInspirationImage = (id: string, attachmentId: string) =>
  blob(
    path(id, `inspiration/${attachmentId}/image/`),
    {},
    "Could not load the image"
  )
export const requestDesign = (
  id: string,
  body: DesignBrief & { conversions: string[] }
) =>
  unwrap<Plan3DJob>(
    request.api.post(path(id, "designs/"), body),
    "Could not start the design"
  )
export const approveDesign = (
  id: string,
  designId: string,
  rowVersion: number
) =>
  unwrap<Plan3DJob>(
    request.api.post(path(id, `designs/${designId}/approve/`), {
      expected_row_version: rowVersion
    }),
    "Could not approve"
  )

export const requestRenders = (id: string) =>
  unwrap<Plan3DJob>(
    request.api.post(path(id, "renders/")),
    "Could not start the renders"
  )
export const retryRender = (id: string, renderId: string, hint?: string) =>
  unwrap<Plan3DJob>(
    request.api.post(
      path(id, `renders/${renderId}/retry/`),
      hint ? { hint } : {}
    ),
    "Could not retry"
  )
export const fetchRenderImage = (
  id: string,
  renderId: string,
  thumb: boolean
) =>
  blob(
    path(id, `renders/${renderId}/image/`),
    thumb ? { thumb: 1 } : {},
    "Could not load the render"
  )
export const downloadRender = (id: string, renderId: string, view: ViewType) =>
  download(
    path(id, `renders/${renderId}/image/`),
    { download: 1 },
    `${view}.png`
  )

export const createChangeRequest = (
  id: string,
  body: { text: string; target_views: ViewType[] }
) =>
  unwrap<ChangeRequest>(
    request.api.post(path(id, "change-requests/"), body),
    "Could not send the change"
  )
export const confirmChange = (
  id: string,
  changeId: string,
  rowVersion: number
) =>
  unwrap<Plan3DJob>(
    request.api.post(path(id, `change-requests/${changeId}/confirm/`), {
      expected_row_version: rowVersion
    }),
    "Could not apply the change"
  )
/** Routes a FAILED change again; 202 answers the change, back to PROPOSED with no classification. */
export const routeChangeAgain = (id: string, changeId: string) =>
  unwrap<ChangeRequest>(
    request.api.post(path(id, `change-requests/${changeId}/route-again/`)),
    "Could not check the change again"
  )
export const cancelChange = (id: string, changeId: string) =>
  unwrap<ChangeRequest>(
    request.api.post(path(id, `change-requests/${changeId}/cancel/`)),
    "Could not cancel"
  )

export const approveFinal = (id: string, rowVersion: number) =>
  unwrap<Plan3DJob>(
    request.api.post(path(id, "approve/"), {
      expected_row_version: rowVersion
    }),
    "Could not approve"
  )
const versionCall =
  (act: string, failure: string) =>
  (id: string, setId: string, rowVersion: number) =>
    unwrap<Plan3DJob>(
      request.api.post(path(id, `versions/${setId}/${act}/`), {
        expected_row_version: rowVersion
      }),
      failure
    )
export const makeVersionOfficial = versionCall(
  "official",
  "Could not make this version Official"
)
export const keepVersion = versionCall("keep", "Could not save this version")
export const discardVersion = versionCall(
  "discard",
  "Could not discard this version"
)
export const downloadOutput = (id: string, outputId: string, name: string) =>
  download(path(id, `outputs/${outputId}/download/`), {}, name)
export const updateSharePackage = (
  id: string,
  body: Partial<Pick<SharePackage, "title" | "message_draft">>
) =>
  unwrap<SharePackage>(
    request.api.patch(path(id, "share-package/"), body),
    "Could not save"
  )

export const getHistory = (id: string) =>
  unwrap<HistoryEvent[]>(
    request.api.get(path(id, "history/")),
    "Could not load history"
  )
export const getDiagnostics = (id: string) =>
  unwrap<Diagnostics>(
    request.api.get(path(id, "diagnostics/")),
    "Could not load diagnostics"
  )
export const cancelJob = (id: string) =>
  unwrap<Plan3DJob>(request.api.post(path(id, "cancel/")), "Could not cancel")
export const retryJob = (id: string) =>
  unwrap<Plan3DJob>(request.api.post(path(id, "retry/")), "Could not retry")
export const getMyPermissions = () =>
  unwrap<MyPermissions>(
    request.api.get(`${BASE_URL}/me/`),
    "Could not load permissions"
  )
export const getRenderingSettings = () =>
  unwrap<RenderingSettings>(
    request.api.get(`${BASE_URL}/settings/`),
    "Could not load settings"
  )
/** Platform administrators only: anyone else gets a 403 ApiError with code `platform_only`. */
export const saveRenderingSettings = (body: RenderingSettingsInput) =>
  unwrap<RenderingSettings>(
    request.api.post(`${BASE_URL}/settings/`, body),
    "Could not save settings"
  )
